import 'chart.js/auto';
import 'chartjs-adapter-dayjs-4/dist/chartjs-adapter-dayjs-4.esm';
import { FunctionComponent, ReactNode, useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { Flex } from 'antd';
import { ChartData, ChartOptions } from 'chart.js';
import { TFunction } from 'i18next';
import { useStore } from '@hub/helpers/use-store';
import { formatDate } from '@monorepo/client-common';
import {
  Card,
  Statistic,
  TextComponent,
  Tooltip,
} from '@monorepo/react-components';
import { LoadingState, useLoading } from '@monorepo/react-hooks';
import {
  AccountBalanceChart,
  Application,
  UnavailableValue,
} from '@monorepo/types';
import { AccountBalanceTooltipContent } from '../../dashboard-common-new/account-balance/account-balance-tooltip';
import DashboardTooltip from '../dashboard-tooltip/dashboard-tooltip';

import './account-balance.scss';

const getTextByApplication = (
  application: Application,
  t: TFunction<['dashboardTemplates']>,
): { equity: string; chartTooltip?: string | ReactNode; pnl: string } => {
  const mapApplicationText: {
    [key: string]: {
      equity: string;
      chartTooltip?: string | ReactNode;
      pnl: string;
    };
  } = {
    [Application.THE5ERS]: {
      equity: t('dashboardTemplates:equity'),
      pnl: t('dashboardTemplates:pnl'),
      chartTooltip: (
        <Flex vertical gap={5}>
          <TextComponent>
            {t('dashboardTemplates:balance_chart_tooltip')}
          </TextComponent>
          <TextComponent>
            {t('dashboardTemplates:equity_chart_tooltip')}
          </TextComponent>
          <TextComponent>
            {t('dashboardTemplates:pnl_chart_tooltip')}
          </TextComponent>
        </Flex>
      ),
    },
    [Application.TTP]: {
      pnl: t('dashboardTemplates:daily_pnl'),
      equity: t('dashboardTemplates:unrealized_pnl'),
      chartTooltip: (
        <Flex vertical gap={5}>
          <TextComponent>
            ·{t('dashboardTemplates:pnl')} -{' '}
            {t('dashboardTemplates:profit_and_loss')}
          </TextComponent>
          <TextComponent>
            ·{t('dashboardTemplates:base_balance')} -{' '}
            {t('dashboardTemplates:starting_balance')}
          </TextComponent>
          <TextComponent>
            ·{t('dashboardTemplates:credits')} -{' '}
            {t('dashboardTemplates:valid_profits_from_previous_account')}
          </TextComponent>
          <TextComponent>
            ·{t('dashboardTemplates:invalid_pnl_message_ttp')}
          </TextComponent>
          <TextComponent>
            ·{t('dashboardTemplates:unrealized_pnl')} -{' '}
            {t('dashboardTemplates:open_profit_and_loss_positions')}
          </TextComponent>
        </Flex>
      ),
    },
  };

  return mapApplicationText[application];
};

const createTooltipContent = (
  value: number,
  invalidValue: number,
  label: string,
  invalidLabel: string,
  invalidMessage?: string,
) => (
  <Flex gap={8} vertical={true}>
    <TextComponent level={7}>
      <Statistic value={value} prefix={'$'} precision={2} />({label})
      {invalidValue !== 0 && (
        <>
          {' '}
          {invalidValue >= 0 ? '-' : '+'}{' '}
          <Statistic
            value={Math.abs(invalidValue)}
            prefix={'$'}
            precision={2}
          />{' '}
          ({invalidLabel})
        </>
      )}
    </TextComponent>
    {invalidMessage && (
      <TextComponent level={7}>{invalidMessage}</TextComponent>
    )}
  </Flex>
);

interface Props {
  accountId: string;
  baseBalance?: number;
  equity: number;
  balance: number;
  unrealized?: number;
  profitAndLoss: number;
  dailyProfitAndLoss?: number;
  application: Application;
  invalidProfitAndLoss: number;
  dailyInvalidProfitAndLoss?: number;
  balanceChart?: AccountBalanceChart[];
  credits?: number;
  withdrawalAmount?: number;
}

const AccountBalance: FunctionComponent<Props> = ({
  accountId,
  baseBalance = 0,
  equity,
  unrealized,
  balance,
  profitAndLoss,
  dailyProfitAndLoss = 0,
  application,
  invalidProfitAndLoss = 0,
  dailyInvalidProfitAndLoss: _dailyInvalidProfitAndLoss = 0,
  credits = 0,
  balanceChart,
  withdrawalAmount = 0,
}) => {
  const [chartData, setChartData] = useState<{ x: number; y: number }[]>([]);

  const [accountBalanceChart, setAccountBalanceChart] = useState<
    AccountBalanceChart[]
  >([]);

  const {
    dataStore: { accountStore },
  } = useStore();

  const { loadingState, updateLoadingState } = useLoading();
  const { t } = useTranslation(['dashboardTemplates']);

  const invalidPnlMessage = {
    [Application.THE5ERS]: t('dashboardTemplates:invalid_pnl_message_the5ers'),
    [Application.TTP]: t('dashboardTemplates:invalid_pnl_message_ttp'),
  };

  useEffect(() => {
    const init = async () => {
      try {
        updateLoadingState(LoadingState.Loading);
        let response: AccountBalanceChart[] = balanceChart || [];

        if (response.length === 0) {
          response = await accountStore.getBalanceChart(accountId);
        }

        setAccountBalanceChart(response);

        const normalized = response.map((tick, i) => {
          return {
            x: i,
            y: Number(tick.value.toFixed(2)),
          };
        });

        setChartData(normalized);
      } catch (e) {
        console.error(`Failed loading account p&l`, e);
      } finally {
        updateLoadingState(LoadingState.Loaded);
      }
    };

    init();
  }, [accountId, balanceChart]);

  const isLoading = loadingState === LoadingState.Loading;

  const data: ChartData<'line'> = {
    datasets: [
      {
        data: [{ x: -1, y: baseBalance }, ...chartData],
        fill: 'start',
        tension: 0.5,
        pointRadius: 5,
        pointBackgroundColor: [
          'transparent',
          ...accountBalanceChart.map((item) =>
            item.type === 'position' ? 'rgb(85,160,218)' : 'yellow',
          ),
        ],
        backgroundColor: 'rgba(64,172,213,0.2)',
        borderColor: 'rgb(85,160,218)',
      },
    ],
  };

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: 0,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          title: function (context) {
            const item = accountBalanceChart[context[0].dataIndex - 1];

            if (!item) {
              return UnavailableValue.Dash;
            }

            return formatDate(item.date, { template: 'monthDayTime' });
          },
          label: function (context) {
            let label = context.dataset.label || '';

            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
              }).format(context.parsed.y);
            }
            return label;
          },
        },
        mode: 'nearest',
      },
    },
    elements: {
      point: {
        radius: 0,
      },
    },
    interaction: {
      intersect: false,
      mode: 'index',
    },
    hover: {
      mode: 'point',
    },
    scales: {
      x: {
        ticks: {
          autoSkip: true,
          maxTicksLimit: 20,
        },
        display: false,
        type: 'linear',
      },
      y: {
        border: {
          display: false,
        },
        ticks: {
          callback: function (value) {
            return `$${Intl.NumberFormat('en-US').format(Number(value))}`;
          },
        },
      },
    },
  };

  const StatValue: FunctionComponent<{
    value: number;
    positiveColor?: boolean;
  }> = ({ value }) => {
    const isPositiveValue = value > 0;
    return (
      <TextComponent
        level={3}
        weight={500}
        className={isPositiveValue ? 'stat-value--profit' : ''}
      >
        <Statistic
          value={value}
          precision={2}
          prefix={isPositiveValue ? '+$' : '$'}
        />
      </TextComponent>
    );
  };

  const StatTitle: FunctionComponent<{ title: string }> = ({ title }) => (
    <TextComponent level={8} responsive={false} className="stat-title">
      {title}
    </TextComponent>
  );

  const getTooltipByApplication = (application: Application) => {
    const mapApplicationTooltip = {
      [Application.THE5ERS]: {
        pnl: createTooltipContent(
          profitAndLoss + invalidProfitAndLoss,
          invalidProfitAndLoss,
          t('dashboardTemplates:total_pnl'),
          t('dashboardTemplates:invalid_trades'),
          invalidPnlMessage[Application.THE5ERS],
        ),
        balance: null,
      },
      [Application.TTP]: {
        pnl: createTooltipContent(
          dailyProfitAndLoss,
          0,
          t('dashboardTemplates:total_daily_pnl'),
          t('dashboardTemplates:invalid_daily_trades'),
        ),
        balance: (
          <AccountBalanceTooltipContent
            baseBalance={baseBalance}
            pnl={profitAndLoss}
            invalidPnl={invalidProfitAndLoss}
            credits={credits}
            withdrawalAmount={withdrawalAmount}
          />
        ),
      },
    };

    return mapApplicationTooltip[application];
  };

  const profitAndLossByApplication =
    application === Application.THE5ERS ? profitAndLoss : dailyProfitAndLoss;

  const equityByApplication =
    application === Application.THE5ERS ? equity : unrealized;

  const balanceByApplication =
    application === Application.THE5ERS
      ? balance
      : balance - invalidProfitAndLoss;

  const stats = [
    {
      title: getTextByApplication(application, t).pnl,
      tooltip: getTooltipByApplication(application).pnl,
      value: profitAndLossByApplication,
      positiveColor: true,
    },
    {
      title: getTextByApplication(application, t).equity,
      value: equityByApplication,
    },
    {
      title: t('dashboardTemplates:balance'),
      value: balanceByApplication,
      tooltip:
        application === Application.THE5ERS
          ? null
          : getTooltipByApplication(application).balance,
    },
  ];

  return (
    <Card
      loading={isLoading}
      className="account-profit-and-loss"
      hoverable={false}
    >
      <Flex vertical gap={12}>
        <Flex justify="space-between" align="center" wrap="wrap">
          <TextComponent level={4} noWrap>
            {t('dashboardTemplates:account_balance')}
          </TextComponent>

          <DashboardTooltip
            absolute={false}
            tooltipText={getTextByApplication(application, t).chartTooltip}
          >
            {application === Application.THE5ERS && (
              <TextComponent level={8}>
                {t('dashboardTemplates:what_is_this_difference')}
              </TextComponent>
            )}
          </DashboardTooltip>
        </Flex>

        <Flex gap={20} align="center" wrap="wrap">
          {stats.map((stat, index) => {
            return (
              <Flex vertical gap={4} key={index}>
                <Tooltip
                  title={stat.tooltip}
                  className={stat.tooltip ? 'balance-tooltip' : ''}
                >
                  <StatTitle title={stat.title} />
                  <StatValue
                    value={stat.value ? stat.value : 0}
                    positiveColor={stat.positiveColor}
                  />
                </Tooltip>
              </Flex>
            );
          })}
        </Flex>
        <div className="account-profit-and-loss__chart">
          <Line data={data} options={options} />
        </div>
      </Flex>
    </Card>
  );
};

export default AccountBalance;
